
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { motion } from "framer-motion";
import { Play, ArrowRight } from "lucide-react";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { api } from "@/lib/api";
import { fadeUp, stagger, scaleIn } from "@/lib/animations";
import type { Video, VideosResponse } from "@/lib/videos";
import VideoCard from "@/components/videos/VideoCard";

export default function ContentSection() {
  const theme = usePalette();
  const [videos, setVideos] = useState<Video[]>([]);

  useEffect(() => {
    api
      .get<VideosResponse>("/api/videos?sort=recent&limit=4")
      .then((r) => setVideos(r.videos))
      .catch(() => setVideos([]));
  }, []);

  const features = [
    {
      title: "Latest Sewing Project",
      desc: "Made this cereal box print outfit from scratch — matching top and pants!",
      tag: "New Make",
      color: theme.accent,
    },
    {
      title: "Favorite Craft Haul",
      desc: "My top picks from the latest quilting convention. So many amazing finds!",
      tag: "Haul",
      color: theme.warm,
    },
    {
      title: "Behind the Scenes",
      desc: "What a day in my life looks like when I'm filming, sewing, and creating content.",
      tag: "Vlog",
      color: theme.pop,
    },
    {
      title: "Product Round-Up",
      desc: "The tools, fabrics, and supplies I genuinely use and love for every project.",
      tag: "Favorites",
      color: colors.denimBlue,
    },
  ];

  return (
    <section
      id="content"
      className="py-24 px-6 relative"
      style={{ backgroundColor: colors.cream }}
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={stagger}
          className="text-center mb-16"
        >
          <motion.p
            variants={fadeUp}
            custom={0}
            className="text-xs tracking-[0.2em] uppercase mb-3"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              color: theme.accent,
            }}
          >
            What I Create
          </motion.p>
          <motion.h2
            variants={fadeUp}
            custom={1}
            className="text-4xl lg:text-5xl mb-4"
            style={{
              fontFamily: fonts.logo,
              fontWeight: 400,
              color: colors.navy,
            }}
          >
            Content That{" "}
            <span style={{ fontFamily: fonts.heading, color: theme.warm }}>
              Sparks
            </span>{" "}
            Something
          </motion.h2>
          <motion.p
            variants={fadeUp}
            custom={2}
            className="max-w-xl mx-auto"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: `${colors.navy}88`,
              letterSpacing: "0.03em",
            }}
          >
            I create content across a few of my favorite worlds — here&apos;s
            what you can expect.
          </motion.p>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-40px" }}
          variants={stagger}
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {videos.length > 0
            ? videos.map((video, i) => <VideoCard key={video.id} video={video} index={i} />)
            : features.map((item, i) => (
                <motion.div
                  key={item.title}
                  variants={scaleIn}
                  custom={i}
                  className="rounded-2xl p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                  style={{ backgroundColor: theme.accentSoft }}
                >
                  <div
                    className="w-full aspect-square rounded-xl mb-4 flex items-center justify-center overflow-hidden transition-transform duration-300 group-hover:scale-[1.02]"
                    style={{ backgroundColor: `${item.color}12` }}
                  >
                    <Play
                      size={32}
                      className="transition-transform duration-300 group-hover:scale-125"
                      style={{ color: item.color }}
                    />
                  </div>
                  <span
                    className="inline-block px-3 py-1 rounded-full text-[10px] tracking-widest uppercase mb-2"
                    style={{
                      fontFamily: fonts.body,
                      fontWeight: 700,
                      backgroundColor: `${item.color}12`,
                      color: item.color,
                    }}
                  >
                    {item.tag}
                  </span>
                  <h4
                    className="text-base mb-1"
                    style={{
                      fontFamily: fonts.body,
                      fontWeight: 700,
                      color: colors.navy,
                    }}
                  >
                    {item.title}
                  </h4>
                  <p
                    className="text-xs leading-relaxed"
                    style={{
                      fontFamily: fonts.body,
                      fontWeight: 500,
                      color: `${colors.navy}77`,
                      letterSpacing: "0.03em",
                    }}
                  >
                    {item.desc}
                  </p>
                </motion.div>
              ))}
        </motion.div>

        <div className="mt-12 text-center">
          <Link
            to="/videos"
            className="inline-flex items-center gap-2 text-sm transition-opacity hover:opacity-70"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              color: theme.accent,
              letterSpacing: "0.05em",
            }}
          >
            Watch all videos <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
